import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import MailRoundedIcon from "@mui/icons-material/MailRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import FolderSharedRoundedIcon from "@mui/icons-material/FolderSharedRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";

import { useSystemStats } from "../api/hooks";

const COUNT_FORMATTER = new Intl.NumberFormat("it-IT");
const BYTE_FORMATTER = new Intl.NumberFormat("it-IT", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
});
const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB", "PB"];

function formatNumber(value: number | null | undefined): string {
  return COUNT_FORMATTER.format(value ?? 0);
}

function formatBytes(value: number | null | undefined): string {
  if (!value) return "0 B";
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${BYTE_FORMATTER.format(size)} ${BYTE_UNITS[unitIndex]}`;
}

function formatDateOnly(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("it-IT");
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("it-IT");
}

function formatRange(start?: string | null, end?: string | null): string {
  if (!start && !end) return "";
  const s = start ? formatDateOnly(start) : "";
  const e = end ? formatDateOnly(end) : "";
  if (s && e) return s === e ? s : `${s} - ${e}`;
  return s || e;
}

function summarizeFolders(folders: Record<string, number> | undefined): string {
  if (!folders) return "";
  const entries = Object.entries(folders);
  if (!entries.length) return "";
  entries.sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0));
  return entries
    .slice(0, 3)
    .map(([name, count]) => `${name} (${formatNumber(count)})`)
    .join(", ");
}

function MetricCard({
  label,
  value,
  icon,
  accent
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  accent?: "primary" | "success" | "error" | "info";
}) {
  return (
    <Card
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        height: "100%"
      }}
    >
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: (theme) => theme.palette[accent || "primary"].light,
            color: (theme) => theme.palette[accent || "primary"].contrastText
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export function Statistics() {
  const { data, isLoading, isError, error, refetch, isFetching } = useSystemStats();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    const description = error instanceof Error ? error.message : "Impossibile caricare le statistiche.";
    return (
      <Stack spacing={2}>
        <Alert severity="error">{description}</Alert>
        <Box>
          <Button variant="contained" onClick={() => refetch()} startIcon={<RefreshRoundedIcon />}>
            Riprova
          </Button>
        </Box>
      </Stack>
    );
  }

  const windowLabel = formatRange(data.earliest_archive_date, data.latest_archive_date);
  const accounts = [...data.accounts].sort((a, b) => b.messages - a.messages);
  const yearly = [...data.yearly].sort((a, b) => Number(b.year) - Number(a.year));
  const scheduler = data.scheduler_status;

  const topErrors = [...accounts].sort((a, b) => b.errors - a.errors).slice(0, 3);
  const topSize = [...accounts].sort((a, b) => b.content_size_bytes - a.content_size_bytes).slice(0, 5);
  const topThroughput = [...accounts]
    .map((acc) => ({
      ...acc,
      daily_bytes: acc.archive_days > 0 ? (acc.content_size_bytes || 0) / acc.archive_days : 0
    }))
    .sort((a, b) => b.daily_bytes - a.daily_bytes)
    .slice(0, 5);

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" gutterBottom>
            Statistiche di Sistema
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generato il {formatDateTime(data.generated_at)} • Base path: {data.base_path}
          </Typography>
          {windowLabel ? (
            <Typography variant="body2" color="text.secondary">
              Periodo archiviato: {windowLabel}
            </Typography>
          ) : null}
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshRoundedIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Aggiorna
        </Button>
      </Stack>

      {data.total_errors > 0 ? (
        <Alert severity="warning">
          Registrati {formatNumber(data.total_errors)} errori complessivi nelle esecuzioni di archiviazione.
        </Alert>
      ) : null}

      {data.accounts_total === 0 ? (
        <Alert severity="info">Nessun archivio presente. Esegui un job per popolare le statistiche.</Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard label="Account totali" value={formatNumber(data.accounts_total)} icon={<FolderSharedRoundedIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard label="Giorni archiviati" value={formatNumber(data.archive_days_total)} icon={<CalendarMonthRoundedIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard label="Messaggi indicizzati" value={formatNumber(data.total_messages)} icon={<MailRoundedIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard label="Errori registrati" value={formatNumber(data.total_errors)} icon={<ErrorOutlineRoundedIcon />} accent="error" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard label="Contenuto indicizzato" value={formatBytes(data.content_size_bytes)} icon={<TimelineRoundedIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard label="Dimensione archivi" value={formatBytes(data.archive_size_bytes)} icon={<DownloadRoundedIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard label="Occupazione su disco" value={formatBytes(data.filesystem_bytes)} icon={<StorageRoundedIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard label="Periodo coperto" value={windowLabel || "-"} icon={<CalendarMonthRoundedIcon />} />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Stato Scheduler
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {scheduler ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Attivo
                </Typography>
                <Typography variant="subtitle1">{scheduler.scheduler_running ? "Sì" : "No"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Job in corso
                </Typography>
                <Typography variant="subtitle1">{scheduler.job_in_progress ? "Sì" : "No"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Prossimo run giornaliero
                </Typography>
                <Typography variant="subtitle1">{formatDateTime(scheduler.next_daily_run_iso ?? scheduler.next_run_iso)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Source-sync
                </Typography>
                <Typography variant="subtitle1">
                  {scheduler.source_sync_enabled
                    ? (scheduler.source_sync_interval_hours === 1 ? "Attivo (ogni ora)" : `Attivo (ogni ${scheduler.source_sync_interval_hours ?? 6} ore)`)
                    : "Disabilitato"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Modalità
                </Typography>
                <Typography variant="subtitle1">{scheduler.backup_mode ?? ""}</Typography>
              </Grid>
              {scheduler.source_sync_enabled ? (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Prossimo source-sync
                  </Typography>
                  <Typography variant="subtitle1">{formatDateTime(scheduler.next_source_sync_iso)}</Typography>
                </Grid>
              ) : null}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Ultimo avvio
                </Typography>
                <Typography variant="subtitle1">{formatDateTime(scheduler.last_run_started_iso)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Ultimo completamento
                </Typography>
                <Typography variant="subtitle1">{formatDateTime(scheduler.last_run_completed_iso)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Account configurati
                </Typography>
                <Typography variant="subtitle1">{formatNumber(scheduler.accounts_total)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Base path
                </Typography>
                <Typography variant="subtitle1">{scheduler.base_path}</Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nessuna informazione sullo scheduler.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Occupazione per casella
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {topSize.length === 0 ? (
                <Alert severity="info">Nessun dato disponibile.</Alert>
              ) : (
                <Stack spacing={2}>
                  {topSize.map((acc) => {
                    const max = topSize[0]?.content_size_bytes || 1;
                    const ratio = Math.min(100, Math.round(((acc.content_size_bytes || 0) / max) * 100));
                    return (
                      <Stack key={acc.name} spacing={0.5}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography fontWeight={600}>{acc.label ?? acc.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatBytes(acc.content_size_bytes)}
                          </Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={ratio} />
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top account per errori
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {topErrors.length === 0 ? (
                <Alert severity="info">Nessun errore registrato.</Alert>
              ) : (
                <Stack spacing={2}>
                  {topErrors.map((acc) => {
                    const max = topErrors[0]?.errors || 1;
                    const ratio = Math.min(100, Math.round(((acc.errors || 0) / max) * 100));
                    return (
                      <Stack key={acc.name} spacing={0.5}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography fontWeight={600}>{acc.label ?? acc.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatNumber(acc.errors)}
                          </Typography>
                        </Stack>
                        <LinearProgress color="error" variant="determinate" value={ratio} />
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Traffico medio giornaliero (per casella)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {topThroughput.length === 0 ? (
                <Alert severity="info">Nessun dato disponibile.</Alert>
              ) : (
                <Stack spacing={2}>
                  {topThroughput.map((acc) => {
                    const max = topThroughput[0]?.daily_bytes || 1;
                    const ratio = Math.min(100, Math.round(((acc.daily_bytes || 0) / max) * 100));
                    return (
                      <Stack key={acc.name} spacing={0.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography fontWeight={600}>{acc.label ?? acc.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatBytes(acc.daily_bytes)} / giorno ({formatNumber(acc.archive_days)} giorni)
                          </Typography>
                        </Stack>
                        <LinearProgress color="info" variant="determinate" value={ratio} />
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Statistiche per Account
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Giorni</TableCell>
                  <TableCell align="right">Messaggi</TableCell>
                  <TableCell align="right">Contenuto</TableCell>
                  <TableCell align="right">Archivi</TableCell>
                  <TableCell align="right">Errori</TableCell>
                  <TableCell>Intervallo</TableCell>
                  <TableCell>Cartelle principali</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.name} hover>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography fontWeight={600}>{account.label ?? account.name}</Typography>
                        {account.label ? (
                          <Typography variant="caption" color="text.secondary">
                            {account.name}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{formatNumber(account.archive_days)}</TableCell>
                    <TableCell align="right">{formatNumber(account.messages)}</TableCell>
                    <TableCell align="right">{formatBytes(account.content_size_bytes)}</TableCell>
                    <TableCell align="right">{formatBytes(account.archive_size_bytes)}</TableCell>
                    <TableCell align="right">{formatNumber(account.errors)}</TableCell>
                    <TableCell>{formatRange(account.first_archive_date, account.last_archive_date)}</TableCell>
                    <TableCell>{summarizeFolders(account.folders)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Aggregati Annuali
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Anno</TableCell>
                  <TableCell align="right">Account</TableCell>
                  <TableCell align="right">Giorni</TableCell>
                  <TableCell align="right">Messaggi</TableCell>
                  <TableCell align="right">Contenuto</TableCell>
                  <TableCell align="right">Archivi</TableCell>
                  <TableCell align="right">Errori</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {yearly.map((year) => (
                  <TableRow key={year.year} hover>
                    <TableCell>{year.year}</TableCell>
                    <TableCell align="right">{formatNumber(year.accounts)}</TableCell>
                    <TableCell align="right">{formatNumber(year.archive_days)}</TableCell>
                    <TableCell align="right">{formatNumber(year.messages)}</TableCell>
                    <TableCell align="right">{formatBytes(year.content_size_bytes)}</TableCell>
                    <TableCell align="right">{formatBytes(year.archive_size_bytes)}</TableCell>
                    <TableCell align="right">{formatNumber(year.errors)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Stack>
  );
}
